import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { realLinkProps } from '../components/links/realLinkProps';
import { useRouterNavigate } from '../components/links/useEntityLinkProps';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import {
  makeStyles,
  tokens,
  Text,
  Badge,
  Spinner,
  MessageBar,
  MessageBarBody,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableCellLayout,
  Button,
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Toaster,
  Toast,
  ToastTitle,
  useToastController,
  useId,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@fluentui/react-components';
import { ArrowDownloadRegular, ArrowLeftRegular, ChevronDownRegular, ChevronRightRegular, MoneyCalculatorRegular } from '@fluentui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useTask, useTaskExtracts, useTaskSources, useMarkFilingFiled, useReopenFiling, useCancelTask, useReopenTask, downloadFilingAttachment, ingestSource, removeSourceStatementById, computeMissingDraft, ApiError, extractKeys, sourceKeys, taskKeys, amountKeysFor, filedAmounts, type ComputedDraftOutcome, type MissingDraftOutcome, type MissingDraftResult } from '../services/complianceApi';
import SendClientUpdateDialog from '../components/compliance/SendClientUpdateDialog';
import ReconcileObligationCard from '../components/compliance/ReconcileObligationCard';
import { attachmentDownloadName } from '../utils/attachmentDownloadName';
import UploadExtractControl from '../components/compliance/UploadExtractControl';
import SourcesBlock, { type SourcesUploadingFile } from '../components/compliance/SourcesBlock';
import ClientTaxRateConfigDialog from '../components/compliance/ClientTaxRateConfigDialog';
import ReopenChoiceDialog from '../components/compliance/ReopenChoiceDialog';
import FilingVersionHistory from '../components/compliance/FilingVersionHistory';
import { NameMismatchWarning } from '../components/compliance/NameMismatchWarning';
import type { ComplianceFiling, TaskAmountExtract, IngestSourceResponse, SourceStatement, NameMismatchAck } from '../types';
import { unsupportedFormLabel } from '../utils/obligationHelpers';
import { formatDate, formatCurrency, formatWholeDay, daysUntil, isTaskOverdue } from '../utils/complianceFormatters';
import { lineVariance } from '../utils/reconcileVariance';

import { firstIncompletePhase, phaseSummary, type TaskPhase } from './taskPhaseState';
import { finalizeRowState } from '../utils/finalizeRowState';
import { shouldGateMarkFiled, resolveMarkFiledAck, type NameGuardEntry, type NameGuardMap } from '../utils/nameGuard';
import { apiErrorMessage } from '../lib/apiErrorMessage';
import { useStateTaxClient } from '../services/stateTaxClientsApi';
import ClientIdentityBlock from '../components/common/ClientIdentityBlock';
import { pageShell } from '../components/layout/pageShell';
import TaskStatusBadge from '../components/compliance/TaskStatusBadge';

/**
 * How each source-removal outcome is reported. Removing one POS statement always invalidates the
 * computed draft (it was summed across ALL statements); the server says whether it managed to
 * rebuild it from the survivors, and if not, why.
 *
 * Every outcome gets its OWN copy and its own severity. A single shared "removed" message was the
 * original defect: a cleared draft and a successfully recomputed one looked identical, so figures
 * disappearing off the screen read as normal operation. The `Cleared*` cases are warnings — the
 * removal itself worked, but the preparer now has something to do before this filing is ready.
 */
const REMOVE_OUTCOME_UI: Record<ComputedDraftOutcome, { key: string; intent: 'success' | 'warning' }> = {
  Unchanged: { key: 'ingestSource.removeOutcome.unchanged', intent: 'success' },
  Recomputed: { key: 'ingestSource.removeOutcome.recomputed', intent: 'success' },
  ClearedNoSourcesLeft: { key: 'ingestSource.removeOutcome.clearedNoSourcesLeft', intent: 'warning' },
  ClearedComputeUnsupported: { key: 'ingestSource.removeOutcome.clearedComputeUnsupported', intent: 'warning' },
  ClearedComputeFailed: { key: 'ingestSource.removeOutcome.clearedComputeFailed', intent: 'warning' },
};

/**
 * Copy for each outcome of computing the draft after the county rate is saved (conversion fix #1).
 * The old single message — "upload the source report again to retry" — sent staff to re-upload a
 * report that was already stored, and a re-upload APPENDS: the next draft sums both copies.
 */
type DraftOutcomeUi = Record<MissingDraftOutcome, { key: string; intent: 'success' | 'warning'; noReasonKey?: string }>;

const RATE_SAVED_OUTCOME_UI: DraftOutcomeUi = {
  Computed: { key: 'rateConfigDialog.afterSave.computed', intent: 'success' },
  DraftExists: { key: 'rateConfigDialog.afterSave.draftExists', intent: 'warning' },
  NoSources: { key: 'rateConfigDialog.afterSave.noSources', intent: 'success' },
  ComputeUnsupported: { key: 'rateConfigDialog.afterSave.computeUnsupported', intent: 'success' },
  Declined: { key: 'rateConfigDialog.afterSave.declined', intent: 'warning', noReasonKey: 'rateConfigDialog.afterSave.declinedNoReason' },
  // Fix #1b (ruled 2026-10-21): the rate was saved from THIS task, so its existing draft is refreshed.
  Refreshed: { key: 'rateConfigDialog.afterSave.refreshed', intent: 'success' },
  Unchanged: { key: 'rateConfigDialog.afterSave.unchanged', intent: 'success' },
  RefreshDeclined: { key: 'rateConfigDialog.afterSave.refreshDeclined', intent: 'warning', noReasonKey: 'rateConfigDialog.afterSave.refreshDeclinedNoReason' },
  RefreshNoSources: { key: 'rateConfigDialog.afterSave.refreshNoSources', intent: 'warning' },
};

/**
 * Fix #1b: the same outcomes when a person presses "Recalculate draft" on one filing, without the
 * "Rate saved." preamble. `DraftExists` is never the answer to a refresh; it is mapped so an older
 * API's reply still says plainly that nothing was recalculated.
 */
const RECALCULATE_OUTCOME_UI: DraftOutcomeUi = {
  Computed: { key: 'recalculateDraft.outcome.computed', intent: 'success' },
  DraftExists: { key: 'recalculateDraft.outcome.notRecalculated', intent: 'warning' },
  NoSources: { key: 'recalculateDraft.outcome.noSources', intent: 'warning' },
  ComputeUnsupported: { key: 'recalculateDraft.outcome.computeUnsupported', intent: 'warning' },
  Declined: { key: 'recalculateDraft.outcome.declined', intent: 'warning', noReasonKey: 'recalculateDraft.outcome.declinedNoReason' },
  Refreshed: { key: 'recalculateDraft.outcome.refreshed', intent: 'success' },
  Unchanged: { key: 'recalculateDraft.outcome.unchanged', intent: 'success' },
  RefreshDeclined: { key: 'recalculateDraft.outcome.refreshDeclined', intent: 'warning', noReasonKey: 'recalculateDraft.outcome.refreshDeclinedNoReason' },
  RefreshNoSources: { key: 'recalculateDraft.outcome.refreshNoSources', intent: 'warning' },
};

const useStyles = makeStyles({
  page: {
    ...pageShell,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
  },
  title: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: '1.1',
    margin: 0,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
  },
  backButton: { alignSelf: 'flex-start' },
  // Inner grid inside the .card utility — the utility provides padding + visual
  // chrome (background, radius, shadow), this handles the field layout.
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  fieldLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontWeight: tokens.fontWeightSemibold,
  },
  fieldValue: {
    fontVariantNumeric: 'tabular-nums',
    fontSize: tokens.fontSizeBase300,
  },
  // The same red and wording the task list uses for a past-due open task.
  overdue: { color: tokens.colorPaletteRedForeground1 },
  overdueNote: { color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200 },
  tableWrapper: {
    overflowX: 'auto',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  reconHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: tokens.spacingVerticalM,
  },
  reconTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  reconTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  reconHint: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  amountChips: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalXS, maxHeight: '4.5rem', overflowY: 'auto' },
  amountChip: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground2, whiteSpace: 'nowrap' },
  reconSection: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalS,
    overflow: 'hidden',
  },
  reconSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    width: '100%',
    padding: tokens.spacingVerticalS,
    border: 'none',
    backgroundColor: tokens.colorNeutralBackground2,
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: tokens.fontSizeBase300,
    ':hover': { backgroundColor: tokens.colorNeutralBackground2Hover },
  },
  reconSectionName: { fontWeight: tokens.fontWeightSemibold, flexGrow: 1 },
  reconSectionBody: { padding: tokens.spacingVerticalS },
});

// Mirrors ReconcileObligationCard's own reconciled test (filed.totalDue, or legacy amounts.totalDue).
const isObligationReconciled = (ex: TaskAmountExtract | undefined) =>
  typeof (ex?.filed?.totalDue ?? ex?.amounts?.totalDue) === 'number';

// Monotonic upload-batch counter — prefixes per-file progress-row ids so rows stay unique
// across batches even when filenames repeat (see handleMultiIngest).
let uploadBatchSeq = 0;

export default function ComplianceTaskDetailPage() {
  const styles = useStyles();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('clientId') ?? '';
  const linkTo = useRouterNavigate();

  const { data: task, isLoading, isError, error } = useTask(id ?? '', clientId);
  // The client record, for the DBA and the outbound edit links. Read LIVE from Dataverse
  // rather than taken from the task: `task.clientName` is denormalised onto the task at
  // GENERATION time, so a name or DBA that changed since would be stale here. Same id space —
  // `task.clientId` is the stateTaxClientId this endpoint keys on.
  const { data: taskClient } = useStateTaxClient(clientId);
  // STX-PROD-05: these load separately from the task. Their failures are rendered below — a failed
  // load used to read as "no draft yet" (`extracts ?? []`), a silent blank on the money screen.
  const { data: extracts, isError: extractsFailed } = useTaskExtracts(id ?? '');
  const { data: sources, isError: sourcesFailed } = useTaskSources(id ?? '');

  // Reset the manually-toggled accordion phase when navigating to a different task so the
  // new task auto-follows its own firstIncompletePhase instead of inheriting the previous
  // task's toggled state.
  useEffect(() => { setOpenPhase(undefined); }, [task?.id]);

  // Send-success toast — per-page Toaster (same pattern as ptc-supervision's detail pages;
  // this repo otherwise uses MessageBar, but a dialog that closes on success has no
  // surviving surface for one, so a toast is the right vehicle here).
  const toasterId = useId('task-detail-toaster');
  const { dispatchToast } = useToastController(toasterId);
  const notifySendSuccess = () =>
    dispatchToast(
      <Toast>
        <ToastTitle>{t('taskDetailPage.sendUpdate.sentToast')}</ToastTitle>
      </Toast>,
      { intent: 'success' },
    );

  const [sendOpen, setSendOpen] = useState(false);
  // sendInitial: when set, pre-seeds the dialog to a specific filing; undefined = default
  // selection (all filed-not-yet-sent). Set by per-filing re-send button; cleared by the
  // header "Send update to client" button so it always opens with the default selection.
  const [sendInitial, setSendInitial] = useState<string | undefined>(undefined);
  const [rateConfigOpen, setRateConfigOpen] = useState(false);
  // The filing whose ingest stopped on "set the county rate first" — saving the rate computes
  // THAT filing's draft from the source it already stored (and no other filing's).
  const [rateConfigFor, setRateConfigFor] = useState<ComplianceFiling | null>(null);
  // The filing whose "Recalculate draft" is in flight (fix #1b), so its button can't be pressed twice.
  const [recalculatingId, setRecalculatingId] = useState<string | null>(null);

  const qc = useQueryClient();
  const mark = useMarkFilingFiled();
  const reopen = useReopenFiling();
  const cancelTask = useCancelTask();
  const reopenTask = useReopenTask();
  // Task-level cancel confirmation dialog. null = closed.
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [taskActionMsg, setTaskActionMsg] = useState<string | null>(null);
  const { accounts } = useMsal();
  const [extractMsg, setExtractMsg] = useState<{ intent: 'success' | 'warning' | 'error'; text: string } | null>(null);
  const [finalizeMsg, setFinalizeMsg] = useState<string | null>(null);

  // Flow A name-mismatch guard, keyed per-obligation: a task can carry multiple filings, and
  // each obligation's most recent extraction is tracked independently so a mismatch on one
  // obligation never overwrites/hides another's (see NameGuardEntry for field semantics).
  const [nameGuards, setNameGuards] = useState<NameGuardMap>(new Map());
  // Mark-as-Filed confirm-to-override dialog target — the obligationId pending confirmation,
  // or null when closed.
  const [markFiledConfirmTarget, setMarkFiledConfirmTarget] = useState<string | null>(null);

  // Non-sales-tax extracted figures: obligationId → figures map.
  // Populated when an ingest returns a non-compute response (figures.figures).
  const [nonSalesTaxFigures, setNonSalesTaxFigures] = useState<Map<string, Record<string, number | null>>>(new Map());
  // Per-obligation, per-file progress rows for a multi-file source drop. Success rows
  // disappear (the refreshed statements list takes over); failed rows stay inline with
  // their error until the next drop on that obligation replaces the batch.
  const [uploadingSources, setUploadingSources] = useState<Map<string, SourcesUploadingFile[]>>(new Map());
  // Statement ids with an in-flight id-based remove (disables that row's ✕).
  const [removingStatementIds, setRemovingStatementIds] = useState<string[]>([]);
  // Clear non-sales-tax figures + upload progress when navigating to a different task.
  useEffect(() => { setNonSalesTaxFigures(new Map()); setUploadingSources(new Map()); }, [task?.id]);

  // Reopen dialog state — single nullable target; null = closed, non-null = open for that obligationId.
  const [reopenTarget, setReopenTarget] = useState<string | null>(null);

  // Reconcile: one obligation card open at a time (collapsible). null = all collapsed. Cards stay
  // mounted (CSS-hidden) so in-progress edits aren't lost when collapsing.
  const [openObligation, setOpenObligation] = useState<string | null>(null);
  useEffect(() => { setOpenObligation(null); }, [task?.id]);

  // Map obligationId → existing extract row for O(1) lookup in the panel.
  const extractByObligation = useMemo(() => {
    const out = new Map<string, TaskAmountExtract>();
    for (const e of extracts ?? []) out.set(e.obligationId, e);
    return out;
  }, [extracts]);

  // Map obligationId → source statements for the SourcesBlock list in the Prepare panel.
  const sourcesByObligation = useMemo(() => {
    const out = new Map<string, SourceStatement[]>();
    for (const s of sources ?? []) out.set(s.obligationId, s.statements ?? []);
    return out;
  }, [sources]);

  // Blob names that are POS sources (Prepare step). A source upload also lands as a
  // return-kind FilingDocument sharing the blob, so exclude these from the Reconcile
  // attachment indicator — Reconcile should surface only genuine return/payment docs.
  // Phase accordion — controlled open state. openPhase starts undefined so the panel
  // auto-follows firstIncompletePhase until the user explicitly toggles.
  // openItems is an array; we keep at most one item open (collapsible single-select).
  const [openPhase, setOpenPhase] = useState<TaskPhase | undefined>(undefined);
  const activePhase = task ? (openPhase ?? firstIncompletePhase(task, extractByObligation)) : undefined;
  const openItems: TaskPhase[] = activePhase ? [activePhase] : [];
  const summary = task ? phaseSummary(task, extractByObligation) : { prepareComputedCount: 0, reconciledCount: 0, filedCount: 0, total: 0 };

  async function handleDownloadAttachment(filing: ComplianceFiling) {
    if (!task) return;
    try {
      const { blob, fileName } = await downloadFilingAttachment(
        task.id, filing.obligationId, task.clientId);
      // Trigger the browser download via a temporary object URL + invisible anchor.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Legacy/system attachments are stored with opaque GUID names; derive a meaningful,
      // always-.pdf name from the obligation + period instead of trusting the stored name.
      a.download = attachmentDownloadName(
        filing.obligationDisplayName, task.periodLabel, filing.attachmentFileName ?? fileName);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      // The table row doesn't have a dedicated error slot yet — surface via alert so the
      // preparer sees it. Better UX is a toast; not worth hauling in the Fluent Toaster for
      // one edge case.
      alert(err instanceof Error ? err.message : t('taskDetailPage.downloadFailed'));
    }
  }

  // Fires the mark-filed mutation. `ack` is set only when the staffer confirmed the
  // name-mismatch override dialog for this obligation — omitted (undefined), the backend
  // simply leaves the filing's nameMismatchAck null, same as any filing with no guard hit.
  function submitMarkFiled(f: ComplianceFiling, ex: TaskAmountExtract | undefined, ack?: NameMismatchAck) {
    if (!task) return;
    mark.mutate({
      taskId: task.id,
      obligationId: f.obligationId,
      payload: {
        clientId: task.clientId,
        confirmationNumber: ex?.confirmationNumber ?? '',
        filedBy: accounts[0]?.name ?? accounts[0]?.username ?? '',
        bankDebitAmount: ex?.amountPaid ?? undefined,
        bankDebitDate: ex?.debitDate ? ex.debitDate.slice(0, 10) : undefined,
        bankDebitAccountMasked: ex?.bankAccountMasked ?? undefined,
        notes: ex?.notes ?? undefined,
        nameMismatchAck: ack,
      },
    }, {
      // Filing truly succeeded — this obligation's mismatch (if any) is resolved, so it's
      // safe to drop the guard entry now (mirrors Flow C's post-save reset, just per-
      // obligation instead of dialog-wide). On failure the entry is deliberately left
      // alone — see the confirm-override handler below for why.
      onSuccess: () => {
        setNameGuards((prev) => {
          if (!prev.has(f.obligationId)) return prev;
          const next = new Map(prev);
          next.delete(f.obligationId);
          return next;
        });
      },
      onError: () => setFinalizeMsg(t('taskDetailPage.finalize.lockFailed')),
    });
  }

  // Shared handling of an ingest response (single pick/drop AND multi-file drop reuse this).
  // The source statement is persisted on every successful ingest, so always refresh first.
  const handleIngestResult = (f: ComplianceFiling, r: IngestSourceResponse) => {
    if (!task) return;
    void qc.invalidateQueries({ queryKey: extractKeys.byTask(task.id) });
    void qc.invalidateQueries({ queryKey: sourceKeys.byTask(task.id) });
    // Narrow: ReturnComputation has `warnings`; non-compute shape has `figures`.
    if ('warnings' in r) {
      if (r.needsRateConfig) {
        setRateConfigFor(f);
        setRateConfigOpen(true);
        setExtractMsg({ intent: 'warning', text: r.warnings[0] ?? t('ingestSource.needsRateConfig') });
        return;
      }
      if (!r.ok) {
        setExtractMsg({ intent: 'error', text: r.warnings[0] ?? t('ingestSource.failed') });
        return;
      }
      setExtractMsg({ intent: r.warnings.length ? 'warning' : 'success', text: r.warnings[0] ?? t('ingestSource.computed') });
    } else {
      setExtractMsg({ intent: r.figures?.ok ? 'success' : 'warning', text: r.figures?.message ?? t('ingestSource.computed') });
      if (r.figures?.figures) {
        const fig = r.figures.figures;
        setNonSalesTaxFigures((prev) => new Map(prev).set(f.obligationId, fig));
      }
    }
  };

  /**
   * One sentence per draft outcome. The engine's reason is server-generated and rendered VERBATIM
   * inside the localized sentence: it names the exact thing still missing. `Refreshed` lists every
   * changed line before → after, in the form's own line order, because "visibly" was the condition
   * of the ruling (fix #1b). Null for an outcome this build doesn't know.
   */
  function draftResultMessage(r: MissingDraftResult, f: ComplianceFiling, table: DraftOutcomeUi) {
    const ui = table[r.outcome];
    if (!ui) return null;
    const reason = r.message?.trim();
    if (ui.noReasonKey) {
      return { intent: ui.intent, text: reason ? t(ui.key, { reason }) : t(ui.noReasonKey) };
    }
    if (r.outcome === 'Refreshed') {
      const order = amountKeysFor(f.obligationType);
      const rank = (k: string) => (order.indexOf(k) < 0 ? order.length : order.indexOf(k));
      const changes = Object.keys({ ...(r.before ?? {}), ...(r.after ?? {}) })
        .sort((a, b) => rank(a) - rank(b))
        .map((k) => `${t(`amounts.${k}`, { defaultValue: k })} ${formatCurrency(r.before?.[k] ?? null)} → ${formatCurrency(r.after?.[k] ?? null)}`)
        .join(' · ');
      return { intent: ui.intent, text: t(ui.key, { changes }) };
    }
    return { intent: ui.intent, text: t(ui.key) };
  }

  // Conversion fix #1: the rate is saved, so compute the waiting filing's draft from the source it
  // already stored. Fix #1b (ruled 2026-10-21): it was saved from THIS task, so an existing draft on
  // that one filing is refreshed too, and the message shows what changed. Never another task.
  async function handleRateSaved() {
    const f = rateConfigFor;
    setRateConfigFor(null);
    if (!task || !f) {
      setExtractMsg({ intent: 'success', text: t('rateConfigDialog.saved') });
      return;
    }
    try {
      const r = await computeMissingDraft(task.id, f.obligationId, task.clientId, { refresh: true });
      void qc.invalidateQueries({ queryKey: extractKeys.byTask(task.id) });
      // Unknown outcome (older/newer API) degrades to the plain "rate saved" copy.
      setExtractMsg(draftResultMessage(r, f, RATE_SAVED_OUTCOME_UI)
        ?? { intent: 'success', text: t('rateConfigDialog.saved') });
    } catch {
      setExtractMsg({ intent: 'error', text: t('rateConfigDialog.afterSave.failed') });
    }
  }

  // Fix #1b: "Recalculate draft" on ONE filing: its draft from the sources already stored, at the
  // current rates (a rate corrected on the Obligations page, say). Only this filing; the server
  // never reaches another task, and a refresh that can't run leaves the draft as it was.
  async function handleRecalculate(f: ComplianceFiling) {
    if (!task) return;
    setRecalculatingId(f.obligationId);
    try {
      const r = await computeMissingDraft(task.id, f.obligationId, task.clientId, { refresh: true });
      void qc.invalidateQueries({ queryKey: extractKeys.byTask(task.id) });
      setExtractMsg(draftResultMessage(r, f, RECALCULATE_OUTCOME_UI)
        ?? { intent: 'warning', text: t('recalculateDraft.outcome.notRecalculated') });
    } catch {
      setExtractMsg({ intent: 'error', text: t('recalculateDraft.outcome.failed') });
    } finally {
      setRecalculatingId(null);
    }
  }

  // Sources v2: any number of files fans out to sequential ingests (no slots, no cap
  // client-side — the server enforces a soft cap and answers 409 when it's hit).
  // One failed file never blocks its siblings.
  async function handleMultiIngest(f: ComplianceFiling, files: File[]) {
    if (!task) return;
    const obligationId = f.obligationId;
    // Start a fresh batch: one progress row per file (replaces any stale error rows).
    // Rows get a per-batch unique id — NOT the filename — so two same-named files in one
    // batch keep distinct rows and success/error lands on the right one.
    const batchId = ++uploadBatchSeq;
    const rows: SourcesUploadingFile[] = files.map((file, index) => ({ id: `${batchId}-${index}`, fileName: file.name }));
    setUploadingSources((prev) => new Map(prev).set(obligationId, rows));
    for (let index = 0; index < files.length; index++) {
      const file = files[index];
      const rowId = rows[index].id;
      try {
        const r = await ingestSource(task.id, obligationId, {
          clientId: task.clientId, file, sourceKind: 'pos_report',
          enteredByName: accounts[0]?.name, enteredById: accounts[0]?.localAccountId,
        });
        handleIngestResult(f, r);
        // Success — drop this file's progress row; the refetched statements list shows it.
        setUploadingSources((prev) => new Map(prev).set(
          obligationId,
          (prev.get(obligationId) ?? []).filter((u) => u.id !== rowId),
        ));
      } catch (err) {
        // Per-file inline error; siblings continue. 409 = source cap OR finalized filing —
        // disambiguated by the server's machine-readable code (cap is the legacy default for
        // older responses without one).
        const message = err instanceof ApiError && err.status === 409
          ? (err.code === 'finalized' ? t('ingestSource.finalized') : t('sourcesBlock.capReached'))
          : err instanceof Error && err.message
            ? err.message
            : t('sourcesBlock.uploadFailed');
        setUploadingSources((prev) => new Map(prev).set(
          obligationId,
          (prev.get(obligationId) ?? []).map((u) => (u.id === rowId ? { ...u, error: message } : u)),
        ));
      }
    }
  }

  // Sources v2: id-based statement remove. Invalidates the same queries the legacy
  // slot-remove used so figures re-sum and the computed draft is refetched.
  //
  // The response says what became of the computed draft, and that verdict is the whole point:
  // on `Recomputed` the refetch repopulates the figures, but on every `Cleared*` it comes back
  // empty. Reporting all of those as one green "removed" is what made a wiped draft look like
  // normal operation.
  async function handleRemoveStatement(f: ComplianceFiling, statementId: string) {
    if (!task) return;
    setRemovingStatementIds((prev) => [...prev, statementId]);
    try {
      const outcome = await removeSourceStatementById(task.id, f.obligationId, statementId, task.clientId);
      void qc.invalidateQueries({ queryKey: extractKeys.byTask(task.id) });
      void qc.invalidateQueries({ queryKey: sourceKeys.byTask(task.id) });
      void qc.invalidateQueries({ queryKey: taskKeys.detail(task.id, task.clientId) });

      // Unknown outcome (older/newer API) degrades to the neutral "nothing to update" copy
      // rather than throwing — the removal itself did succeed.
      const ui = REMOVE_OUTCOME_UI[outcome.computedDraft] ?? REMOVE_OUTCOME_UI.Unchanged;
      // The engine's hard-stop reason (missing county surtax rate, etc.) is server-generated and
      // is rendered VERBATIM inside the localized sentence — it names the exact thing to fix, so
      // paraphrasing it would throw away the only actionable detail in the whole flow.
      const reason = outcome.computedDraftMessage?.trim();
      // BL-114 (ruled by Christian): the rate-config stop is said in the reader's language, with its own next step. The
      // stored reports rebuild the draft once the rate is saved here, so it never says "upload again" (a re-upload APPENDS).
      const needsRate = outcome.computedDraft === 'ClearedComputeFailed' && outcome.computedDraftNeedsRateConfig === true;
      const failedWithoutReason = outcome.computedDraft === 'ClearedComputeFailed' && !reason;
      setExtractMsg({
        intent: ui.intent,
        text: needsRate
          ? t('ingestSource.removeOutcome.clearedNeedsRateConfig', { reason: t('ingestSource.needsRateConfig') })
          : failedWithoutReason
          ? t('ingestSource.removeOutcome.clearedComputeFailedNoReason')
          : outcome.computedDraft === 'ClearedComputeFailed'
            ? t(ui.key, { reason })
            : outcome.computedDraft === 'ClearedComputeUnsupported'
              ? t(ui.key, { form: unsupportedFormLabel(f) })
              : t(ui.key),
      });
    } catch (err) {
      setExtractMsg({ intent: 'error', text: err instanceof Error ? err.message : t('ingestSource.failed') });
    } finally {
      setRemovingStatementIds((prev) => prev.filter((id) => id !== statementId));
    }
  }

  const filedCount = useMemo(
    () => (task ? task.filings.filter((f) => !!f.filedAt).length : 0),
    [task],
  );

  const dash = t('common.dash');

  if (!id || !clientId) {
    return (
      <div className={styles.page}>
        <MessageBar intent="error">
          <MessageBarBody>{t('taskDetailPage.missingIds')}</MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Toaster toasterId={toasterId} position="top-end" />
      <Button
        as="a"
        appearance="subtle"
        icon={<ArrowLeftRegular />}
        className={styles.backButton}
        {...realLinkProps('/compliance/tasks', { navigate: linkTo })}
      >
        {t('common.backToTasks')}
      </Button>

      {isLoading && <Spinner label={t('taskDetailPage.loading')} />}

      {isError && (
        <MessageBar intent="error">
          <MessageBarBody>
            {apiErrorMessage(error, t, 'taskDetailPage.loadError')}
          </MessageBarBody>
        </MessageBar>
      )}

      {!isError && (extractsFailed || sourcesFailed) && (
        <MessageBar intent="error" data-testid="figures-load-error">
          <MessageBarBody>{t('taskDetailPage.figuresLoadError')}</MessageBarBody>
        </MessageBar>
      )}

      {!isLoading && !isError && task && (
        <>
          <div className={styles.header}>
            <div className={styles.titleRow}>
              <div>
                <ClientIdentityBlock
                  name={taskClient?.accountName ?? task.clientName}
                  fictitiousName={taskClient?.fictitiousName}
                  accountId={taskClient?.accountId}
                  stateTaxClientId={task.clientId}
                >
                  <Text className={styles.title}>{task.clientName}</Text>
                </ClientIdentityBlock>
                <div className={styles.subtitle}>{task.periodLabel}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacingHorizontalM)' }}>
                {task.status === 'cancelled' ? (
                  <Button
                    appearance="secondary"
                    disabled={reopenTask.isPending}
                    onClick={() => {
                      setTaskActionMsg(null);
                      reopenTask.mutate(
                        { taskId: task.id, clientId: task.clientId },
                        { onError: (err) => setTaskActionMsg(err instanceof Error ? err.message : t('taskDetailPage.cancelTask.reopenFailed')) },
                      );
                    }}
                  >
                    {t('taskDetailPage.cancelTask.reopenButton')}
                  </Button>
                ) : (
                  // Cancellable while non-terminal and nothing filed yet — lets staff undo an
                  // early-created task without losing it.
                  !['filed', 'client_notified'].includes(task.status)
                  && !task.filings.some((f) => !!f.filedAt) && (
                    <Button
                      appearance="subtle"
                      disabled={cancelTask.isPending}
                      onClick={() => { setTaskActionMsg(null); setCancelConfirmOpen(true); }}
                    >
                      {t('taskDetailPage.cancelTask.cancelButton')}
                    </Button>
                  )
                )}
                <Button
                  appearance="primary"
                  disabled={!task.filings.some((f) => !!f.filedAt)}
                  onClick={() => { setSendInitial(undefined); setSendOpen(true); }}
                >
                  {t('taskDetailPage.sendUpdate.openButton')}
                </Button>
                <TaskStatusBadge task={task} size="large" />
              </div>
            </div>
          </div>

          {taskActionMsg && (
            <MessageBar intent="error">
              <MessageBarBody>{taskActionMsg}</MessageBarBody>
            </MessageBar>
          )}

          <div className="card">
            <div className={styles.summaryGrid}>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>{t('taskDetailPage.fields.period')}</span>
                <span className={styles.fieldValue}>{formatWholeDay(task.periodStartDate)} – {formatWholeDay(task.periodEndDate)}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>{t('taskDetailPage.fields.dueDate')}</span>
                <span className={isTaskOverdue(task) ? `${styles.fieldValue} ${styles.overdue}` : styles.fieldValue}>{formatWholeDay(task.dueDate)}</span>
                {isTaskOverdue(task) && (
                  <span className={styles.overdueNote}>{t('tasksPage.daysOverdue', { count: Math.abs(daysUntil(task.dueDate)) })}</span>
                )}
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>{t('taskDetailPage.fields.filings')}</span>
                <span className={styles.fieldValue}>{t('taskDetailPage.filedCount', { filed: filedCount, total: task.filings.length })}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>{t('taskDetailPage.fields.assignedTo')}</span>
                <span className={styles.fieldValue}>{task.assignedToName ?? dash}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>{t('taskDetailPage.fields.taskFiledAt')}</span>
                <span className={styles.fieldValue}>{formatDate(task.filedAt)}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.fieldLabel}>{t('taskDetailPage.fields.clientNotifiedAt')}</span>
                <span className={styles.fieldValue}>{formatDate(task.clientNotifiedAt)}</span>
              </div>
            </div>
          </div>

          {/* The three steps sit on the same card surface as the summary above (polish pass): they used to float on the page. */}
          <div className="card" data-testid="task-phases-card">
          <Accordion
            openItems={openItems}
            onToggle={(_, data) => setOpenPhase((data.openItems[0] as TaskPhase) ?? undefined)}
            collapsible
          >
            {/* ── 1 · Prepare ─────────────────────────────────────────────────── */}
            <AccordionItem value="prepare">
              <AccordionHeader>
                {t('taskDetailPage.phases.prepare.title')}
                {' · '}
                {summary.prepareComputedCount > 0
                  ? t('taskDetailPage.phases.prepare.summary', { count: summary.prepareComputedCount })
                  : t('taskDetailPage.phases.prepare.summaryNone')}
              </AccordionHeader>
              <AccordionPanel>
                {/* Source-report intake for every obligation (Sources v2): a dynamic
                    multi-source list + drop zone per filing, all obligation types alike. */}
                {task.filings.map((f) => {
                  const e = extractByObligation.get(f.obligationId);
                  const computed = e?.computed;
                  const isSalesTax = f.obligationType === 'sales_tax';
                  const statements = sourcesByObligation.get(f.obligationId) ?? [];
                  const finalized = !!f.filedAt;
                  // Reconciliation cross-check signal: a non-zero filed-vs-computed totalDue
                  // variance swaps the SourcesBlock header ✓ for ⚠. Gate on BOTH sides being
                  // present via lineVariance — the persisted variance.totalDue is non-null
                  // whenever EITHER side exists (backend defaults the missing side to 0), which
                  // would flag every computed-not-yet-reconciled filing and every reconciled
                  // non-compute filing.
                  const totalDueVariance = lineVariance(e?.computed, e?.filed, 'totalDue');
                  const warning = totalDueVariance !== null && Math.abs(totalDueVariance) > 0.005
                    ? t('sourcesBlock.varianceWarning')
                    : undefined;
                  return (
                    <div key={f.obligationId} style={{ marginBottom: 'var(--spacingVerticalM)' }}>
                      <div style={{ fontWeight: 'var(--fontWeightSemibold)', marginBottom: '4px' }}>
                        {f.obligationDisplayName}
                      </div>
                      {finalized && (
                        <div style={{ fontSize: 'var(--fontSizeBase200)', color: 'var(--colorPaletteYellowForeground2, #8a6d00)', marginBottom: '4px' }}>
                          {t('ingestSource.finalized')}
                        </div>
                      )}
                      <SourcesBlock
                        taskId={task.id}
                        clientId={task.clientId}
                        obligationId={f.obligationId}
                        obligationType={f.obligationType}
                        statements={statements}
                        finalized={finalized}
                        warning={warning}
                        onIngestFiles={(files) => { void handleMultiIngest(f, files); }}
                        onRemove={(statementId) => { void handleRemoveStatement(f, statementId); }}
                        busyStatementIds={removingStatementIds}
                        uploadingFiles={uploadingSources.get(f.obligationId) ?? []}
                      />
                      {/* Computed draft chips — sales_tax only (from ReturnComputation extract). */}
                      {computed && isSalesTax && (
                        <div className={styles.amountChips} style={{ marginTop: '4px' }}>
                          {amountKeysFor(f.obligationType)
                            .filter((k) => k !== 'totalDue')
                            .map((k) => (
                              <span key={k} className={styles.amountChip}>
                                {t(`amounts.${k}`)}: {formatCurrency(computed[k] ?? null)}
                              </span>
                            ))}
                          <span className={styles.amountChip} style={{ fontWeight: 'var(--fontWeightSemibold)' }}>
                            {t('amounts.totalDue')}: {formatCurrency(computed.totalDue ?? null)}
                          </span>
                        </div>
                      )}
                      {/* Fix #1b: recalculate THIS filing's draft from the sources already stored. Only
                          where there is something to compute from, and never on a filed filing. */}
                      {isSalesTax && !finalized && statements.length > 0 && (
                        <div style={{ marginTop: '4px' }}>
                          <Button
                            size="small"
                            appearance="subtle"
                            icon={<MoneyCalculatorRegular />}
                            disabled={recalculatingId === f.obligationId}
                            title={t('recalculateDraft.hint')}
                            onClick={() => { void handleRecalculate(f); }}
                          >
                            {computed && Object.keys(computed).length > 0
                              ? t('recalculateDraft.button')
                              : t('recalculateDraft.buttonFirst')}
                          </Button>
                        </div>
                      )}
                      {/* Extracted figures chips — non-sales-tax obligations (item J).
                          Shown after an ingest-source upload returns { source, figures }.
                          Sales-tax keeps its computed chips above; this branch is for all others. */}
                      {!isSalesTax && nonSalesTaxFigures.has(f.obligationId) && (() => {
                        const figs = nonSalesTaxFigures.get(f.obligationId)!;
                        const entries = Object.entries(figs);
                        return (
                          <div style={{ marginTop: '4px' }}>
                            {entries.length > 0 && (
                              <div className={styles.amountChips}>
                                {entries.map(([k, v]) => (
                                  <span key={k} className={styles.amountChip}>
                                    {k}: {formatCurrency(v)}
                                  </span>
                                ))}
                              </div>
                            )}
                            <div style={{ fontSize: 'var(--fontSizeBase200)', color: 'var(--colorNeutralForeground3)', marginTop: '4px' }}>
                              {t('ingestSource.extractedNote')}
                            </div>
                          </div>
                        );
                      })()}
                      {/* Per-obligation Flow A mismatch banner — looked up by THIS row's
                          obligationId so a mismatch on one filing never hides/overwrites
                          another's warning. */}
                      {nameGuards.has(f.obligationId) && (
                        <div style={{ marginTop: 'var(--spacingVerticalS)' }}>
                          <NameMismatchWarning
                            show
                            extractedName={nameGuards.get(f.obligationId)?.extractedName}
                            clientName={task.clientName}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
                {extractMsg && (
                  <div data-testid="extract-msg" data-intent={extractMsg.intent} style={{ marginTop: 'var(--spacingVerticalM)' }}>
                    <MessageBar intent={extractMsg.intent}>
                      <MessageBarBody>{extractMsg.text}</MessageBarBody>
                    </MessageBar>
                  </div>
                )}
              </AccordionPanel>
            </AccordionItem>

            {/* ── 2 · Reconcile ───────────────────────────────────────────────── */}
            <AccordionItem value="reconcile">
              <AccordionHeader>
                {t('taskDetailPage.phases.reconcile.title')}
                {' · '}
                {t('taskDetailPage.phases.reconcile.summary', { done: summary.reconciledCount, total: summary.total })}
              </AccordionHeader>
              <AccordionPanel>
                {/* Reconciliation panel — bookkeeping bridge. Producer side (extractor app)
                    and staff manual entry both write to the same Cosmos shape; this panel is
                    the read + edit surface. One row per filing showing the standard DR-15
                    reported amounts. */}
                <div className={styles.reconHeader}>
                  <div className={styles.reconTitleRow}>
                    <MoneyCalculatorRegular />
                    <span className={styles.reconTitle}>{t('taskDetailPage.reconciliation.title')}</span>
                  </div>
                  <span className={styles.reconHint}>
                    {t('taskDetailPage.reconciliation.hint')}
                  </span>
                </div>
                {extractMsg && (
                  <div data-testid="extract-msg" data-intent={extractMsg.intent} style={{ marginBottom: 'var(--spacingVerticalM)' }}>
                    <MessageBar intent={extractMsg.intent}>
                      <MessageBarBody>{extractMsg.text}</MessageBarBody>
                    </MessageBar>
                  </div>
                )}
                {task.filings.map((f) => {
                  const ex = extractByObligation.get(f.obligationId);
                  const open = openObligation === f.obligationId;
                  const reconciled = isObligationReconciled(ex);
                  return (
                  <div key={f.obligationId} className={styles.reconSection}>
                    <button
                      type="button"
                      className={styles.reconSectionHeader}
                      aria-expanded={open}
                      // Pin the Reconcile phase open so an upload that reconciles the last
                      // obligation doesn't auto-advance firstIncompletePhase and collapse the panel.
                      onClick={() => { setOpenPhase('reconcile'); setOpenObligation(open ? null : f.obligationId); }}
                    >
                      {open ? <ChevronDownRegular /> : <ChevronRightRegular />}
                      <span className={styles.reconSectionName}>{f.obligationDisplayName}</span>
                      <Badge appearance="filled" color={reconciled ? 'success' : 'warning'}>
                        {reconciled
                          ? t('taskDetailPage.reconcile.status.reconciled')
                          : t('taskDetailPage.reconcile.status.pending')}
                      </Badge>
                    </button>
                    {/* Per-obligation Flow A mismatch banner — always visible (not gated
                        behind the collapsible body) so a mismatch is visible without
                        expanding, matching the old phase-level banner's visibility, but now
                        scoped to THIS obligationId only. */}
                    {nameGuards.has(f.obligationId) && (
                      <div style={{ marginTop: 'var(--spacingVerticalXS)' }}>
                        <NameMismatchWarning
                          show
                          extractedName={nameGuards.get(f.obligationId)?.extractedName}
                          clientName={task.clientName}
                        />
                      </div>
                    )}
                    <div className={styles.reconSectionBody} style={{ display: open ? 'block' : 'none' }}>
                  <ReconcileObligationCard
                    taskId={task.id}
                    clientId={task.clientId}
                    filing={f}
                    extract={ex}
                  >
                    <UploadExtractControl
                      taskId={task.id}
                      clientId={task.clientId}
                      obligationId={f.obligationId}
                      obligationType={f.obligationType}
                      attachments={f.attachments ?? []}
                      onRemoved={() => {
                        void qc.invalidateQueries({ queryKey: taskKeys.detail(task.id, task.clientId) });
                        void qc.invalidateQueries({ queryKey: extractKeys.byTask(task.id) });
                        setExtractMsg({ intent: 'success', text: t('uploadExtract.removed') });
                      }}
                      onUploaded={() => { void qc.invalidateQueries({ queryKey: taskKeys.all }); }}
                      onExtracted={(r) => {
                        void qc.invalidateQueries({ queryKey: extractKeys.byTask(task.id) });
                        if (!r.ok) { setExtractMsg({ intent: 'error', text: r.message ?? t('uploadExtract.failed') }); return; }
                        // Fail-open: businessNameMatch undefined/true never sets the guard.
                        // A fresh extraction always supersedes any prior guard state for THIS
                        // obligation only — success clears a stale warning + un-acks the next
                        // file, but sibling obligations' entries are untouched (per-obligation
                        // map, not a single overwritten object).
                        if (r.businessNameMatch === false) {
                          const entry: NameGuardEntry = {
                            extractedName: r.extractedBusinessName,
                            score: r.businessNameScore,
                            matchedAgainst: r.matchedAgainst,
                            acknowledged: false,
                          };
                          setNameGuards((prev) => new Map(prev).set(f.obligationId, entry));
                        } else {
                          setNameGuards((prev) => {
                            if (!prev.has(f.obligationId)) return prev;
                            const next = new Map(prev);
                            next.delete(f.obligationId);
                            return next;
                          });
                        }
                        if (!r.taxTypeMatch || !r.crossCheckPassed) setExtractMsg({ intent: 'warning', text: r.message ?? t('uploadExtract.review') });
                        else setExtractMsg({ intent: 'success', text: t('uploadExtract.success') });
                      }}
                    />
                    {/* POS-source compute lives only in Prepare — Reconcile is filed-amount entry. */}
                  </ReconcileObligationCard>
                    </div>
                  </div>
                  );
                })}
              </AccordionPanel>
            </AccordionItem>

            {/* ── 3 · Finalize ────────────────────────────────────────────────── */}
            <AccordionItem value="finalize">
              <AccordionHeader>
                {t('taskDetailPage.phases.finalize.title')}
                {' · '}
                {t('taskDetailPage.phases.finalize.summary', { done: summary.filedCount, total: summary.total })}
              </AccordionHeader>
              <AccordionPanel>
                {finalizeMsg && (
                  <div style={{ marginBottom: 'var(--spacingVerticalM)' }}>
                    <MessageBar intent="error">
                      <MessageBarBody>{finalizeMsg}</MessageBarBody>
                    </MessageBar>
                  </div>
                )}
                <div className={styles.tableWrapper}>
                  <Table aria-label={t('taskDetailPage.fields.filings')}>
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell>{t('taskDetailPage.finalize.columns.obligation')}</TableHeaderCell>
                        <TableHeaderCell>{t('taskDetailPage.finalize.columns.amount')}</TableHeaderCell>
                        <TableHeaderCell>{t('taskDetailPage.finalize.columns.confirmation')}</TableHeaderCell>
                        <TableHeaderCell>{t('taskDetailPage.finalize.columns.status')}</TableHeaderCell>
                        <TableHeaderCell>{t('taskDetailPage.finalize.columns.attachment')}</TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {task.filings.map((f) => {
                        const ex = extractByObligation.get(f.obligationId);
                        const state = finalizeRowState(f, ex);
                        return (
                          <TableRow key={f.obligationId}>
                            <TableCell>
                              <TableCellLayout>
                                <span title={f.obligationType} style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacingHorizontalXS)' }}>
                                  {f.obligationDisplayName}
                                  {f.versionNumber != null && f.versionNumber > 1 && (
                                    <Badge appearance="tint" color="warning" size="small">
                                      {t('taskDetailPage.reopen.amended', { version: f.versionNumber })}
                                    </Badge>
                                  )}
                                </span>
                                {f.priorVersions != null && f.priorVersions.length > 0 && (
                                  <details style={{ marginTop: '4px' }}>
                                    <summary
                                      style={{
                                        fontSize: 'var(--fontSizeBase200)',
                                        color: 'var(--colorNeutralForeground3)',
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                      }}
                                    >
                                      {t('taskDetailPage.finalize.versionHistory')}
                                    </summary>
                                    <div style={{ marginTop: '4px' }}>
                                      <FilingVersionHistory
                                        currentVersion={f.versionNumber ?? 1}
                                        current={{
                                          confirmationNumber: f.confirmationNumber,
                                          amountDue: f.amountDue,
                                          bankDebitAmount: f.bankDebitAmount,
                                          bankDebitDate: f.bankDebitDate,
                                          bankDebitAccountMasked: f.bankDebitAccountMasked,
                                          filedAt: f.filedAt,
                                          filedBy: f.filedBy,
                                        }}
                                        priorVersions={f.priorVersions}
                                      />
                                    </div>
                                  </details>
                                )}
                              </TableCellLayout>
                            </TableCell>
                            <TableCell>
                              <TableCellLayout>
                                {/* The FILED amount only; the automated estimate is labelled, never shown as the amount due (ruling 2026-10-26). */}
                                {formatCurrency(filedAmounts(ex).totalDue ?? null)}
                                {filedAmounts(ex).totalDue == null && typeof ex?.computed?.totalDue === 'number' && (
                                  <Badge appearance="outline" color="subtle" size="small" style={{ marginLeft: 6 }}>
                                    {t('taskDetailPage.finalize.estimateOnly', { amount: formatCurrency(ex.computed.totalDue) })}
                                  </Badge>
                                )}
                                {f.clientHandlesPayment && (
                                  <Badge appearance="tint" color="informative" size="small" style={{ marginLeft: 6 }}>
                                    {t('taskDetailPage.finalize.clientRemits')}
                                  </Badge>
                                )}
                              </TableCellLayout>
                            </TableCell>
                            <TableCell>
                              <TableCellLayout>
                                {ex?.confirmationNumber ?? dash}
                              </TableCellLayout>
                            </TableCell>
                            <TableCell>
                              <TableCellLayout>
                                {state === 'filed' && (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacingHorizontalS)' }}>
                                    <span>{t('taskDetailPage.finalize.filedOn', { date: formatDate(f.filedAt) })}</span>
                                    <Menu>
                                      <MenuTrigger disableButtonEnhancement>
                                        <Button
                                          appearance="secondary"
                                          size="small"
                                          iconPosition="after"
                                          icon={<ChevronDownRegular />}
                                        >
                                          {t('taskDetailPage.finalize.actions')}
                                        </Button>
                                      </MenuTrigger>
                                      <MenuPopover>
                                        <MenuList>
                                          <MenuItem
                                            onClick={() => {
                                              setReopenTarget(f.obligationId);
                                            }}
                                          >
                                            {t('taskDetailPage.finalize.reopen')}
                                          </MenuItem>
                                        </MenuList>
                                      </MenuPopover>
                                    </Menu>
                                  </span>
                                )}
                                {state === 'ready' && (
                                  <Button
                                    appearance="primary"
                                    size="small"
                                    disabled={mark.isPending && mark.variables?.obligationId === f.obligationId}
                                    onClick={() => {
                                      // Gate: an unacknowledged name-mismatch guard on THIS obligation
                                      // routes through the confirm dialog instead of filing directly.
                                      if (shouldGateMarkFiled(nameGuards, f.obligationId)) {
                                        setMarkFiledConfirmTarget(f.obligationId);
                                        return;
                                      }
                                      // Ungated path: either no mismatch ever hit this
                                      // obligation, or it's a retry after a failed override
                                      // submit — resolveMarkFiledAck returns the persisted
                                      // ack in the latter case so the retry doesn't drop it.
                                      submitMarkFiled(f, ex, resolveMarkFiledAck(nameGuards, f.obligationId));
                                    }}
                                  >
                                    {t('taskDetailPage.finalize.markFiled')}
                                  </Button>
                                )}
                                {state === 'needs-reconcile' && (
                                  <span style={{ color: 'var(--colorNeutralForeground3)' }}>
                                    {t('taskDetailPage.finalize.needsReconcile')}
                                  </span>
                                )}
                              </TableCellLayout>
                            </TableCell>
                            <TableCell>
                              <TableCellLayout>
                                {f.attachmentBlobName ? (
                                  <Button
                                    appearance="subtle"
                                    size="small"
                                    icon={<ArrowDownloadRegular />}
                                    aria-label={t('taskDetailPage.finalize.download')}
                                    onClick={() => { void handleDownloadAttachment(f); }}
                                  >
                                    {t('taskDetailPage.finalize.download')}
                                  </Button>
                                ) : (
                                  <span style={{ color: 'var(--colorNeutralForeground3)' }}>{dash}</span>
                                )}
                              </TableCellLayout>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
          </div>

          <SendClientUpdateDialog task={task} initialObligationId={sendInitial} open={sendOpen} onOpenChange={setSendOpen} onSent={notifySendSuccess} />
          {task && (
            <ClientTaxRateConfigDialog
              open={rateConfigOpen}
              onOpenChange={setRateConfigOpen}
              clientId={task.clientId}
              onSaved={() => { void handleRateSaved(); }}
            />
          )}
          <Dialog
            open={cancelConfirmOpen}
            onOpenChange={(_e, d) => { if (!cancelTask.isPending) setCancelConfirmOpen(d.open); }}
          >
            <DialogSurface>
              <DialogBody>
                <DialogTitle>{t('taskDetailPage.cancelTask.confirmTitle')}</DialogTitle>
                <DialogContent>{t('taskDetailPage.cancelTask.confirmBody')}</DialogContent>
                <DialogActions>
                  <Button
                    appearance="secondary"
                    disabled={cancelTask.isPending}
                    onClick={() => setCancelConfirmOpen(false)}
                  >
                    {t('taskDetailPage.cancelTask.keepButton')}
                  </Button>
                  <Button
                    appearance="primary"
                    disabled={cancelTask.isPending}
                    onClick={() => {
                      setTaskActionMsg(null);
                      cancelTask.mutate(
                        { taskId: task.id, clientId: task.clientId },
                        {
                          onSuccess: () => setCancelConfirmOpen(false),
                          onError: (err) => {
                            setCancelConfirmOpen(false);
                            setTaskActionMsg(err instanceof Error ? err.message : t('taskDetailPage.cancelTask.cancelFailed'));
                          },
                        },
                      );
                    }}
                  >
                    {t('taskDetailPage.cancelTask.confirmButton')}
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
          <Dialog
            open={markFiledConfirmTarget !== null}
            onOpenChange={(_e, d) => { if (!d.open) setMarkFiledConfirmTarget(null); }}
          >
            <DialogSurface>
              <DialogBody>
                <DialogTitle>{t('clientDetailPage.nameGuard.confirmTitle')}</DialogTitle>
                <DialogContent>
                  {t('clientDetailPage.nameGuard.confirmBody', {
                    extracted: (markFiledConfirmTarget ? nameGuards.get(markFiledConfirmTarget)?.extractedName : undefined) ?? '',
                    client: task.clientName,
                  })}
                </DialogContent>
                <DialogActions>
                  <Button appearance="secondary" onClick={() => setMarkFiledConfirmTarget(null)}>
                    {t('clientDetailPage.nameGuard.cancel')}
                  </Button>
                  <Button
                    appearance="primary"
                    onClick={() => {
                      const target = markFiledConfirmTarget;
                      const filing = task.filings.find((x) => x.obligationId === target);
                      const entry = target ? nameGuards.get(target) : undefined;
                      if (filing && target && entry) {
                        const ack: NameMismatchAck = {
                          acknowledgedByName: accounts[0]?.name ?? accounts[0]?.username ?? null,
                          acknowledgedAt: new Date().toISOString(),
                          extractedName: entry.extractedName,
                          score: entry.score,
                          matchedAgainst: entry.matchedAgainst,
                        };
                        // Override confirmed — flip `acknowledged` and persist the built ack
                        // on the entry, but do NOT delete it here. submitMarkFiled's
                        // mark.mutate call is async and can fail transiently (its onError
                        // only sets a message); if the map entry were deleted synchronously
                        // like this used to, a Mark-Filed retry after that failure would find
                        // no entry, shouldGateMarkFiled would return false, and the retry
                        // would submit with ack=undefined — silently losing the override audit
                        // trail. Keeping the (now-acknowledged) entry lets a retry resolve the
                        // same ack via resolveMarkFiledAck. The entry is cleared for real once
                        // the filing actually succeeds (see submitMarkFiled's onSuccess).
                        setNameGuards((prev) => new Map(prev).set(target, { ...entry, acknowledged: true, ack }));
                        submitMarkFiled(filing, extractByObligation.get(filing.obligationId), ack);
                      }
                      setMarkFiledConfirmTarget(null);
                    }}
                  >
                    {t('clientDetailPage.nameGuard.confirmOverride')}
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
          <ReopenChoiceDialog
            open={reopenTarget !== null}
            onOpenChange={(open) => { if (!open) setReopenTarget(null); }}
            isPending={reopen.isPending}
            onChoose={(mode) => {
              const target = reopenTarget;
              if (!target) return;
              reopen.mutate(
                { taskId: task.id, obligationId: target, payload: { clientId: task.clientId, mode } },
                {
                  onSuccess: () => { setReopenTarget((curr) => (curr === target ? null : curr)); setFinalizeMsg(null); },
                  onError: () => setFinalizeMsg(t('taskDetailPage.finalize.reopenFailed')),
                },
              );
            }}
          />
        </>
      )}
    </div>
  );
}
